import useIsMobile from '../../hooks/useIsMobile';
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';

const statusConfig = {
  active:    { label: 'Activo',     bg: '#D1FAE5', color: '#065F46' },
  inactive:  { label: 'Inactivo',   bg: '#FEF3C7', color: '#92400E' },
  completed: { label: 'Completado', bg: '#F3F4F6', color: '#374151' },
};

const cfStatusConfig = {
  open:        { label: 'Abierto',    bg: '#DBEAFE', color: '#1D4ED8' },
  in_progress: { label: 'En proceso', bg: '#FEF3C7', color: '#92400E' },
  completed:   { label: 'Completado', bg: '#D1FAE5', color: '#065F46' },
  cancelled:   { label: 'Cancelado',  bg: '#FEE2E2', color: '#991B1B' },
};

const Badge = ({ config }) => (
  <span style={{ display: 'inline-flex', padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, backgroundColor: config?.bg || '#F3F4F6', color: config?.color || '#374151', whiteSpace: 'nowrap' }}>
    {config?.label || '—'}
  </span>
);

const InfoRow = ({ label, value }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', padding: '9px 0', borderBottom: '1px solid #F3F4F6' }}>
    <span style={{ width: 110, flexShrink: 0, fontSize: 12, color: '#9CA3AF', fontWeight: 500, paddingTop: 1 }}>{label}</span>
    <span style={{ fontSize: 13, color: '#111827', fontWeight: 500, flex: 1, wordBreak: 'break-word' }}>{value || <span style={{ color: '#D1D5DB' }}>—</span>}</span>
  </div>
);

const ClientDetail = () => {
  const isMobile = useIsMobile();
  const { id } = useParams();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [caseFiles, setCaseFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auditLog, setAuditLog] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/clients/${id}`),
      api.get('/case-files', { params: { client_id: id } }),
    ]).then(([clientRes, cfRes]) => {
      setClient(clientRes.data);
      setCaseFiles(cfRes.data.data || cfRes.data);
    }).catch(console.error)
      .finally(() => setLoading(false));

    if (user?.role === 'notary') {
      setLoadingAudit(true);
      api.get(`/activity-log/clients/${id}`)
        .then(res => setAuditLog(res.data))
        .catch(console.error)
        .finally(() => setLoadingAudit(false));
    }
  }, [id]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', flexDirection: 'column', gap: 14 }}>
      <div style={{ width: 32, height: 32, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    </div>
  );

  if (!client) return (
    <div style={{ padding: 20, borderRadius: 10, backgroundColor: '#FEE2E2', color: '#991B1B', fontSize: 14 }}>
      Cliente no encontrado
    </div>
  );

  const auditEventConfig = {
    created: { label: 'Creado',    bg: '#D1FAE5', color: '#065F46' },
    updated: { label: 'Editado',   bg: '#DBEAFE', color: '#1D4ED8' },
    deleted: { label: 'Eliminado', bg: '#FEE2E2', color: '#991B1B' },
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14, fontSize: 12, color: '#9CA3AF' }}>
        <Link to="/clients" style={{ color: '#4F63D2', textDecoration: 'none', fontWeight: 500 }}>Clientes</Link>
        <span>›</span>
        <span style={{ color: '#4B5563' }}>{client.name}</span>
      </div>

      <PageHeader
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {client.name}
            <Badge config={statusConfig[client.status]} />
          </div>
        }
        subtitle={client.reason || 'Sin motivo registrado'}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to={`/clients/${id}/edit`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F9FAFB'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
              <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              Editar
            </Link>
            <Link to="/clients"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F9FAFB'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
              <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              Regresar
            </Link>
          </div>
        }
      />

      {/* Aviso desactivado */}
      {!client.active && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px', borderRadius: 10, backgroundColor: '#FEE2E2', border: '1px solid #FECACA', marginBottom: 18 }}>
          <svg style={{ width: 18, height: 18, color: '#EF4444', flexShrink: 0, marginTop: 1 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
          <div>
            <p style={{ margin: '0 0 2px', fontWeight: 600, fontSize: 13, color: '#991B1B' }}>Cliente Desactivado</p>
            <p style={{ margin: 0, fontSize: 13, color: '#B91C1C' }}>{client.deactivation_reason || 'Sin motivo registrado'}</p>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '320px 1fr', gap: 16, alignItems: 'start' }}>

        {/* Panel lateral */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Avatar + datos principales */}
          <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ padding: '20px 18px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#EEF0FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#4F63D2', fontSize: 20, flexShrink: 0 }}>
                {client.name.charAt(0).toUpperCase()}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 2 }}>{client.name}</div>
                <Badge config={statusConfig[client.status]} />
              </div>
            </div>
            <div style={{ padding: '4px 18px 12px' }}>
              <InfoRow label="Teléfono"    value={client.phone} />
              <InfoRow label="Email"       value={client.email} />
              <InfoRow label="Dirección"   value={client.address} />
              <InfoRow label="Responsable" value={client.assigned_user?.name} />
              <InfoRow label="Registro"    value={new Date(client.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })} />
              <div style={{ paddingTop: 10 }}>
                <p style={{ margin: '0 0 4px', fontSize: 12, color: '#9CA3AF', fontWeight: 500 }}>Motivo</p>
                <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{client.reason || '—'}</p>
              </div>
            </div>
          </div>

          {/* Contacto */}
          {client.contact_name && (
            <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
              <div style={{ padding: '12px 18px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Contacto</span>
                {client.contact_role && (
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, backgroundColor: '#DBEAFE', color: '#1D4ED8' }}>{client.contact_role}</span>
                )}
              </div>
              <div style={{ padding: '4px 18px 12px' }}>
                <InfoRow label="Nombre"   value={client.contact_name} />
                <InfoRow label="Teléfono" value={client.contact_phone} />
                <InfoRow label="Email"    value={client.contact_email} />
              </div>
            </div>
          )}
        </div>

        {/* Columna principal */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Expedientes */}
          <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderBottom: '1px solid #F3F4F6' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Expedientes</span>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, backgroundColor: '#EEF0FB', color: '#4F63D2' }}>{caseFiles.length}</span>
              </div>
              <Link to={`/clients/${id}/case-files/create`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 7, backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 12, textDecoration: 'none' }}>
                <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                Nuevo
              </Link>
            </div>

            {caseFiles.length === 0 ? (
              <EmptyState icon="📂" title="No hay expedientes" subtitle="Crea el primer expediente para este cliente" />
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F9FAFB' }}>
                    {['Título', 'Trámite', 'Responsable', 'Docs', 'Tiempo', 'Estado', ''].map((h, i) => (
                      <th key={i} className={[1, 2, 3].includes(i) ? 'mobile-hide' : ''}
                        style={{ padding: '9px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9CA3AF', textAlign: 'left', borderBottom: '1px solid #F3F4F6' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {caseFiles.map(cf => (
                    <tr key={cf.id} style={{ backgroundColor: cf.is_stale ? '#FFFBF0' : '#fff' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFBFF'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = cf.is_stale ? '#FFFBF0' : '#fff'}>
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{cf.title}</div>
                        <div className="d-md-none" style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{cf.procedure_type?.name}</div>
                      </td>
                      <td className="mobile-hide" style={{ padding: '10px 14px', fontSize: 12, color: '#6B7280' }}>{cf.procedure_type?.name || '—'}</td>
                      <td className="mobile-hide" style={{ padding: '10px 14px', fontSize: 12, color: '#6B7280' }}>{cf.assigned_user?.name || '—'}</td>
                      <td className="mobile-hide" style={{ padding: '10px 14px' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, backgroundColor: cf.document_stats?.delivered === cf.document_stats?.total && cf.document_stats?.total > 0 ? '#D1FAE5' : '#FEF3C7', color: cf.document_stats?.delivered === cf.document_stats?.total && cf.document_stats?.total > 0 ? '#065F46' : '#92400E' }}>
                          {cf.document_stats?.delivered}/{cf.document_stats?.total}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, backgroundColor: cf.is_stale ? '#FEE2E2' : '#F3F4F6', color: cf.is_stale ? '#991B1B' : '#374151' }}>
                          {cf.elapsed_time}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <Badge config={cfStatusConfig[cf.status]} />
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <Link to={`/case-files/${cf.id}`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, backgroundColor: '#EEF0FB', color: '#4F63D2', textDecoration: 'none' }}>
                          <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Audit log */}
          {user?.role === 'notary' && (
            <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 18px', borderBottom: '1px solid #F3F4F6' }}>
                <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Log de Auditoría</span>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, backgroundColor: '#F3F4F6', color: '#374151' }}>{auditLog.length}</span>
              </div>
              {loadingAudit ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                  <div style={{ width: 24, height: 24, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                </div>
              ) : auditLog.length === 0 ? (
                <EmptyState icon="📋" title="Sin registros de auditoría" />
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#F9FAFB' }}>
                      {['Acción', 'Por', 'Cambios', 'Fecha'].map((h, i) => (
                        <th key={i} style={{ padding: '9px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9CA3AF', textAlign: 'left', borderBottom: '1px solid #F3F4F6', width: i === 2 ? '45%' : 'auto' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {auditLog.map(log => (
                      <tr key={log.id} style={{ backgroundColor: '#fff' }}>
                        <td style={{ padding: '10px 14px' }}>
                          <Badge config={auditEventConfig[log.event]} />
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#6B7280' }}>{log.causer}</td>
                        <td style={{ padding: '10px 14px', fontSize: 11, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                          {log.old && log.new ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              {Object.keys(log.new).map(key =>
                                log.old[key] !== log.new[key] ? (
                                  <div key={key}>
                                    <span style={{ color: '#9CA3AF' }}>{key}: </span>
                                    <span style={{ textDecoration: 'line-through', color: '#EF4444' }}>{String(log.old[key] ?? '—')}</span>
                                    <span style={{ color: '#9CA3AF' }}> → </span>
                                    <span style={{ color: '#10B981' }}>{String(log.new[key] ?? '—')}</span>
                                  </div>
                                ) : null
                              )}
                            </div>
                          ) : (
                            <span style={{ color: '#9CA3AF' }}>{log.description}</span>
                          )}
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                          {new Date(log.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientDetail;
